import { utils } from 'pixi.js'
import { events } from '@/scripts/config/events'
import { RESIZER } from '@/scripts/config/constants'
import { calculateDimensions } from './dimensionCalculator'
import { getAvailableViewportHeight } from './viewportUtils'

export interface ResizerDimensions {
  containerH: number
  containerR: number
  containerW: number
  scale: number
  stageH: number
  stageW: number
  stageX: number
  stageY: number
}

let emitter = new utils.EventEmitter()
let containerEl: HTMLDivElement | null = null
let ro: ResizeObserver | null = null
let debounceId = 0
let current: ResizerDimensions | undefined

function init(container: HTMLDivElement): void {
  if (containerEl) destroy()
  containerEl = container

  if ('ResizeObserver' in window) {
    ro = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1]
      debouncedCompute(entry)
    })
    // width comes from container; height we'll compute from viewport (not container)
    ro.observe(containerEl as Element, { box: 'border-box' } as ResizeObserverOptions)
  }

  window.addEventListener('resize', handleWindowResize, { passive: true })
  window.addEventListener('orientationchange', handleWindowResize, { passive: true })

  compute()
  requestAnimationFrame(() => compute())
}

function destroy(): void {
  ro?.disconnect()
  ro = null
  window.removeEventListener('resize', handleWindowResize)
  window.removeEventListener('orientationchange', handleWindowResize)

  if (debounceId) {
    clearTimeout(debounceId)
    debounceId = 0
  }

  emitter.removeAllListeners()
  emitter = new utils.EventEmitter()

  containerEl = null
  current = undefined
}

function getDimensions(): ResizerDimensions | undefined {
  return current
}

const on = emitter.on.bind(emitter)
const off =
  emitter.off?.bind(emitter) ?? ((event: string, fn: (...args: unknown[]) => void) => emitter.removeListener(event, fn))

function handleWindowResize() {
  debouncedCompute()
}

function debouncedCompute(entry?: ResizeObserverEntry) {
  if (debounceId) clearTimeout(debounceId)
  debounceId = window.setTimeout(() => compute(entry), RESIZER.DEBOUNCE_DELAY)
}

function compute(entry?: ResizeObserverEntry): void {
  if (!containerEl) return

  // 1) Horizontal cap comes from the container WIDTH (layout)
  let containerW: number
  if (entry) {
    containerW = Math.max(0, Math.round(entry.contentRect.width))
  } else {
    const rect = containerEl.getBoundingClientRect()
    containerW = Math.max(0, Math.round(rect.width))
  }

  // 2) Vertical cap comes from the viewport minus header/footer (NOT container height)
  const availH = getAvailableViewportHeight()

  // 3-8) Calculate dimensions
  const dimensions = calculateDimensions(containerW, availH)

  // 7) Tell the container to match the stage height so header/footer hug the canvas
  //    (We DO NOT read container height for caps, so no feedback loop.)
  containerEl.style.height = `${dimensions.stageH}px`

  // 8) Report back
  document.documentElement.style.setProperty('--canvas-scale', dimensions.scale.toString())
  document.documentElement.style.setProperty('--canvas-width', `${dimensions.stageW.toString()}px`)

  current = dimensions
  ;(Resizer as { dimensions: ResizerDimensions | undefined }).dimensions = current
  emitter.emit(events.RESIZE, current)
}

const Resizer = {
  init,
  destroy,
  on,
  off,
  getDimensions,
  dimensions: current as ResizerDimensions | undefined,
}

export default Resizer
export { init, destroy, on, off, getDimensions }
